import api from './api';
import type {
  PurchaseOrder,
  PurchaseOrderLine,
  GoodsReceipt,
  PaginatedResponse,
  PurchaseOrderStatus,
} from '../types/venue';

const BASE = '/admin/purchase-order';
const GR_BASE = '/admin/goods-receipt';

const purchaseOrderService = {
  getOrders: async (
    venueId: string | undefined,
    page = 1,
    pageSize = 20,
    status?: PurchaseOrderStatus,
    supplierId?: string,
  ): Promise<PaginatedResponse<PurchaseOrder>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (venueId) params.venueId = venueId;
    if (status) params.status = status;
    if (supplierId) params.supplierId = supplierId;
    const response = await api.get(BASE, { params });
    return response.data;
  },

  getOrder: async (poId: string): Promise<PurchaseOrder> => {
    const response = await api.get(`${BASE}/${poId}`);
    return response.data;
  },

  createOrder: async (data: {
    supplierId: string;
    venueId: string;
    expectedDeliveryDate?: string;
    notes?: string;
  }): Promise<PurchaseOrder> => {
    const response = await api.post(BASE, data);
    return response.data;
  },

  updateOrder: async (
    poId: string,
    data: { expectedDeliveryDate?: string | null; notes?: string },
  ): Promise<PurchaseOrder> => {
    const response = await api.put(`${BASE}/${poId}`, data);
    return response.data;
  },

  getLines: async (poId: string): Promise<PaginatedResponse<PurchaseOrderLine>> => {
    const response = await api.get(`${BASE}/${poId}/line`, { params: { pageSize: 500 } });
    return response.data;
  },

  /** Fix 5: aggregated received qty per PO line across all posted receipts. */
  getLineTotals: async (poId: string): Promise<Array<{ purchaseOrderLineId: string; totalReceivedQty: number }>> => {
    const response = await api.get(`${BASE}/${poId}/line-totals`);
    return response.data;
  },

  createLine: async (
    poId: string,
    data: {
      inventoryItemId: string;
      orderedQty: number;
      unitPrice?: number;
    },
  ): Promise<PurchaseOrderLine> => {
    const response = await api.post(`${BASE}/${poId}/line`, data);
    return response.data;
  },

  updateLine: async (
    poId: string,
    lineId: string,
    data: { orderedQty?: number; unitPrice?: number },
  ): Promise<PurchaseOrderLine> => {
    const response = await api.put(`${BASE}/${poId}/line/${lineId}`, data);
    return response.data;
  },

  deleteLine: async (poId: string, lineId: string): Promise<void> => {
    await api.delete(`${BASE}/${poId}/line/${lineId}`);
  },

  markOrdered: async (poId: string): Promise<PurchaseOrder> => {
    const response = await api.post(`${BASE}/${poId}/mark-ordered`);
    return response.data;
  },

  cancel: async (poId: string): Promise<PurchaseOrder> => {
    const response = await api.post(`${BASE}/${poId}/cancel`);
    return response.data;
  },

  close: async (poId: string): Promise<PurchaseOrder> => {
    const response = await api.post(`${BASE}/${poId}/close`);
    return response.data;
  },

  getReceipts: async (poId: string): Promise<PaginatedResponse<GoodsReceipt>> => {
    const response = await api.get(GR_BASE, { params: { purchaseOrderId: poId, pageSize: 100 } });
    return response.data;
  },
};

export default purchaseOrderService;
